import { ContentChip, ExhaustivenessError, ManagementCostParameter } from 'ComponentLibrary'
import { ContractualOptionsType, Rent } from 'ComponentLibrary/src/molecules/Table/Cells/TenantCells/types'
import {
    getTenantRentalStatusColor,
    getTenantUsageTypeColor,
    TENANT_RENTAL_STATUS_LABELS,
    TENANT_USAGE_TYPE_LABELS,
    TenantRentalStatusLabelKeys,
    TenantUsageTypeLabelKeys,
} from 'ComponentLibrary/src/common/tenant'
import { SimpleTableCell } from './SimpleTableCell'
import { SimpleTableMonetaryCell, SimpleTablePercentageCell, SimpleTableTextCell, SimpleTableTextProps } from './SimpleTableTextCells'

type SimpleTableRentCellProps = {
    rent?: Rent
    showPaymentPeriod?: boolean
    locale?: string
    paymentPeriodLabels?: Record<Rent['payment_period'], string>
    paymentScopeLabels?: Record<Rent['payment_scope'], string>
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableRentCell({
    rent,
    showPaymentPeriod,
    paymentPeriodLabels,
    paymentScopeLabels,
    locale,
    ...rest
}: SimpleTableRentCellProps) {
    if (!rent) {
        return <SimpleTableTextCell {...rest} />
    }
    const period = showPaymentPeriod
        ? (paymentPeriodLabels ?? { monthly: 'pro Monat', yearly: 'pro Jahr' })[rent.payment_period]
        : undefined
    const scope = paymentScopeLabels?.[rent.payment_scope] ?? getPaymentScopeText(rent.payment_scope)
    const additionalUnit = [rent.payment_scope === 'absolute' ? ` ${scope}` : scope, period].filter(Boolean).join(' ')
    return <SimpleTableMonetaryCell monetaryValue={rent.value} additionalUnit={additionalUnit} locale={locale} {...rest} />
}

type SimpleTableContractualOptionsCellProps = {
    contractualOptions?: ContractualOptionsType
    yearsLabel?: string
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableContractualOptionsCell({ contractualOptions, yearsLabel, ...rest }: SimpleTableContractualOptionsCellProps) {
    const text = contractualOptions ? `${contractualOptions.frequency}x${contractualOptions.years} ${yearsLabel ?? 'Jahre'}` : undefined
    return <SimpleTableTextCell text={text} {...rest} />
}

type SimpleTableRentalStatusCellProps = {
    rentalStatus?: TenantRentalStatusLabelKeys
    rentalStatusLabels?: Partial<Record<TenantRentalStatusLabelKeys, string>>
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableRentalStatusCell({ rentalStatus, rentalStatusLabels, ...rest }: SimpleTableRentalStatusCellProps) {
    const label = rentalStatus ? (rentalStatusLabels?.[rentalStatus] ?? TENANT_RENTAL_STATUS_LABELS[rentalStatus]) : undefined
    const color = rentalStatus ? getTenantRentalStatusColor(rentalStatus) : undefined
    if (!label || !color) {
        return <SimpleTableTextCell {...rest} />
    }
    return (
        <SimpleTableCell alignment={rest.alignment} highlight={rest.highlight} selected={rest.selected}>
            <ContentChip size="small" color={color} label={label} />
        </SimpleTableCell>
    )
}

type SimpleTableUsageTypeCellProps = {
    usageType?: TenantUsageTypeLabelKeys
    usageTypeLabels?: Partial<Record<TenantUsageTypeLabelKeys, string>>
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableUsageTypeCell({ usageType, usageTypeLabels, ...rest }: SimpleTableUsageTypeCellProps) {
    const label = usageType ? (usageTypeLabels?.[usageType] ?? TENANT_USAGE_TYPE_LABELS[usageType]) : undefined
    const color = usageType ? getTenantUsageTypeColor(usageType) : undefined
    if (!label || !color) {
        return <SimpleTableTextCell {...rest} />
    }
    return (
        <SimpleTableCell alignment={rest.alignment} highlight={rest.highlight} selected={rest.selected} hoverable={rest.hoverable}>
            <ContentChip size="small" color={color} label={label} />
        </SimpleTableCell>
    )
}

type SimpleTableCostCellProps = {
    cost?: ManagementCostParameter
} & Omit<SimpleTableTextProps, 'text' | 'unit'>

export function SimpleTableCostCell({ cost, ...rest }: SimpleTableCostCellProps) {
    if (!cost) {
        return <SimpleTableTextCell {...rest} />
    }
    const costScope = cost.cost_scope
    switch (costScope) {
        case 'absolute':
            return <SimpleTableMonetaryCell monetaryValue={cost.value} {...rest} />
        case 'per_unit':
            return <SimpleTableMonetaryCell monetaryValue={cost.value} additionalUnit="/Einheit" {...rest} />
        case 'per_area':
            return <SimpleTableMonetaryCell monetaryValue={cost.value} additionalUnit="/m²" {...rest} />
        case 'per_construction_cost':
            return <SimpleTablePercentageCell percentage={cost.value} additionalUnit="Baukosten" {...rest} />
        case 'per_yearly_rent':
            return <SimpleTablePercentageCell percentage={cost.value} additionalUnit="Jahresmiete" {...rest} />
        case 'per_monthly_rent':
            return <SimpleTableTextCell text={`${cost.value} x Monatsmieten`} {...rest} />
        default:
            throw ExhaustivenessError(costScope)
    }
}

function getPaymentScopeText(paymentScope: Rent['payment_scope']) {
    switch (paymentScope) {
        case 'per_area':
            return '/m²'
        case 'per_unit':
            return '/Einheit'
        case 'absolute':
            return 'abs.'
        default:
            throw ExhaustivenessError(paymentScope)
    }
}
